<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faTableList,
    faSquareList,
    faMemoPad,
    faClipboardQuestion,
    faMobileScreen,
    faClipboard,
    faHospitalUser,
    faUser,
    faUserGroup,
    faFileLines,
    faSquarePollVertical,
    faDropletPercent,
    faCalendarDays,
    faChevronRight,
    faWeightScale,
    faBars,
    faHeadset,
    faMessageExclamation,
    faFileShield,
    faMessageMiddle,
    faHospital,
    faHouseMedical,
    faBuilding,
    faTriangleExclamation,
  } from '@fortawesome/pro-light-svg-icons';
  import { theme, toggleTheme } from '$stores/themeStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { user, hasRole, userRoles, userReadOnly, userBlinded } from '$stores/sessionStore';
  import { hasAccessUiAction } from '$utils/permissionUtils.js';
  import { currentAssignmentHasSites } from '$utils/userAssignments.js';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import MenuItem from './MenuItem.svelte';
  import { onMount, onDestroy, tick } from 'svelte';
  import { config } from '$stores/configStore';
  import { writable } from 'svelte/store';
  import { navigate as goto } from 'svelte-routing';
  import { tr, le, tui } from '$stores/translationStore';
  import { sidebarCollapsed, toggleSidebar } from '$stores/uiStore';
  import { currentSiteMessageCount } from '$stores/messageCountStore';
  import { currentParticipantMessageCount } from '$stores/participantMessageCountStore';
  import TetrisModal from './TetrisModal.svelte';
  import { currentDatabase as currentDatabaseStore } from '../stores/databaseStore';
  // Using local API endpoints instead of direct API access
  const API_BASE_URL = '/api/proxy';

  let blindedExpanded = false;
  let unblindedExpanded = false;
  let supportExpanded = false;
  let reportsExpanded = false;
  let participantReportsExpanded = false;
  let participantExpanded = false;
  let helpExpanded = false;
  let complianceExpanded = false;
  let currentGlucoseUnit = 'mg/dL';
  let currentTheme = 'dark';
  let sidebarElement;
  let showTetrisModal = false;
  let complianceAnchor;
  let participantReportsAnchor;
  let supportAnchor;
  let reportsAnchor;
  let complianceMenuEl;
  let participantReportsMenuEl;
  let supportMenuEl;
  let reportsMenuEl;
  let submenuPositions = {};
  
  // Database switcher state
  let dbMenuExpanded = false;
  let databases = [];
  let currentDbIndex = null;
  let dbSwitching = false;
  let dbSwitchingTo = null;
  let multiDbMode = false;

  // Message count is now managed by the messageCountStore

  // Store for localization entries
  export const localizationEntries = writable({});

  // Feature flag variables
  let isScheduleEnabled = true;
  let isComplianceEnabled = true;
  let isMessagingEnabled = true;
  let isSiteMessagingEnabled = true;
  let isDiaryEnabled = true;
  let isSMBGEnabled = true;

  // Report flag variables - default to true
  let isCgmDetailsEnabled = true;
  let isCgmReadingsEnabled = true;
  let isSmbgDetailsEnabled = true;
  let isDiaryEntryDetailsEnabled = true;
  let isInsulinDailyTotalsEnabled = true;
  let isAlertsMessageDetailsEnabled = true;
  let isSubjectCgmEnabled = true;
  let isSensorCgmEnabled = true;
  let isCgmStatisticsEnabled = true;
  let isMeasurementsEnabled = true;
  let isAgpDailyProfilesEnabled = true;
  let isEventReportEnabled = false;
  let isPeriodComplianceEnabled = true;
  let isPeriodStatisticsEnabled = true;
  let isCgmSensorStatisticsEnabled = true;
  let isCgmParticipantStatisticsEnabled = true;

  // Computed properties to check if any reports are enabled in each section
  let hasAnyGeneralReports = true;
  let hasAnyParticipantReports = true;
  let hasAnyCustomReports = true;

  // Computed properties to check if user has assigned studies/sites
  $: hasAssignedStudies = $user?.studyIds && $user.studyIds.length > 0;
  $: currentStudyIsSiteScoped = currentAssignmentHasSites($user, $selectedStudy?.protocolNumber);

  $: cgmStatsSidebarRole = hasAccessUiAction(
    'sidebar.visibility.subjectOrSensorCgmStats',
    $userRoles,
    $studyProperties,
    false
  );

  $: canSidebarEventReport =
    $userReadOnly ||
    hasAccessUiAction('sidebar.menu.eventReport', $userRoles, $studyProperties, false);
  $: canSidebarParticipantReports =
    !$userBlinded &&
    ($userReadOnly ||
      hasAccessUiAction('sidebar.menu.participantReports', $userRoles, $studyProperties, false));
  $: canSidebarSupportTools = hasAccessUiAction(
    'sidebar.menu.supportTools',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canSidebarGeneralReportsShell =
    $userReadOnly ||
    hasAccessUiAction('sidebar.menu.generalReportsShell', $userRoles, $studyProperties, false);
  $: canSidebarGeneralReportsBlindedItems =
    !$userBlinded &&
    ($userReadOnly ||
      hasAccessUiAction(
        'sidebar.menu.generalReports.blindedDataItems',
        $userRoles,
        $studyProperties,
        false
      ));
  $: canSidebarSubjectCgmReport = hasAccessUiAction(
    'sidebar.menu.generalReports.subjectCgm',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canSidebarSensorCgmReport = hasAccessUiAction(
    'sidebar.menu.generalReports.sensorCgm',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canSidebarStudyAnalytics = hasAccessUiAction(
    'sidebar.menu.generalReports.studyAnalytics',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );
  $: canFooterChangelogLink = hasAccessUiAction(
    'sidebar.footer.changelogLink',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    // console.log('Loading study properties for study:', $selectedStudy._id);
    fetchStudyProperties($selectedStudy._id);
  } else {
    // console.log('No study selected, clearing study properties');
    studyProperties.set(null);
  }

  // Reactive variables for feature flags
  $: {
    if ($studyProperties) {
      isScheduleEnabled = $studyProperties.features?.schedule === true;
      isComplianceEnabled = $studyProperties.features?.compliance === true;
      isMessagingEnabled = $studyProperties.features?.messaging === true;
      isSiteMessagingEnabled = $studyProperties.features?.siteMessaging !== false;
      isDiaryEnabled = $studyProperties.features?.diary === true;
      isSMBGEnabled = $studyProperties.features?.smbg === true;

      // Report flags - default to true if not configured
      isCgmDetailsEnabled = $studyProperties.reports?.generalReports?.cgmDetails !== false;
      isCgmReadingsEnabled = $studyProperties.reports?.generalReports?.cgmReadings !== false;
      isSmbgDetailsEnabled = $studyProperties.reports?.generalReports?.smbgDetails !== false;
      isDiaryEntryDetailsEnabled =
        $studyProperties.reports?.generalReports?.diaryEntryDetails !== false;
      isInsulinDailyTotalsEnabled =
        $studyProperties.reports?.generalReports?.insulinDailyTotals !== false;
      isAlertsMessageDetailsEnabled =
        $studyProperties.reports?.generalReports?.alertsMessageDetails !== false;
      isSubjectCgmEnabled = $studyProperties.reports?.generalReports?.subjectCgm !== false;
      isSensorCgmEnabled = $studyProperties.reports?.generalReports?.sensorCgm !== false;
      isCgmStatisticsEnabled =
        $studyProperties.reports?.participantReports?.cgmStatistics !== false;
      isMeasurementsEnabled = $studyProperties.reports?.participantReports?.measurements !== false;
      isAgpDailyProfilesEnabled =
        $studyProperties.reports?.participantReports?.agpDailyProfiles !== false;
      isEventReportEnabled = $studyProperties.reports?.generalReports?.eventReport === true;
      isPeriodComplianceEnabled =
        $studyProperties.reports?.customReports?.periodCompliance !== false;
      isPeriodStatisticsEnabled =
        $studyProperties.reports?.customReports?.periodStatistics !== false;
      isCgmSensorStatisticsEnabled =
        $studyProperties.reports?.customReports?.cgmSensorStatistics !== false;
      isCgmParticipantStatisticsEnabled =
        $studyProperties.reports?.customReports?.cgmParticipantStatistics !== false;
    } else {
      // Default to true when no study properties are loaded
      isScheduleEnabled = true;
      isComplianceEnabled = true;
      isMessagingEnabled = true;
      isSiteMessagingEnabled = true;
      isDiaryEnabled = true;
      isSMBGEnabled = true;

      // Report flags default to true
      isCgmDetailsEnabled = true;
      isCgmReadingsEnabled = true;
      isSmbgDetailsEnabled = true;
      isDiaryEntryDetailsEnabled = true;
      isInsulinDailyTotalsEnabled = true;
      isAlertsMessageDetailsEnabled = true;
      isSubjectCgmEnabled = true;
      isSensorCgmEnabled = true;
      isCgmStatisticsEnabled = true;
      isMeasurementsEnabled = true;
      isAgpDailyProfilesEnabled = true;
      isEventReportEnabled = false;
      isPeriodComplianceEnabled = true;
      isPeriodStatisticsEnabled = true;
      isCgmSensorStatisticsEnabled = true;
      isCgmParticipantStatisticsEnabled = true;
    }

    // Calculate if any reports are enabled in each section
    hasAnyGeneralReports =
      isCgmDetailsEnabled ||
      isCgmReadingsEnabled ||
      (isSMBGEnabled && isSmbgDetailsEnabled) ||
      (isDiaryEnabled && isDiaryEntryDetailsEnabled) ||
      (isDiaryEnabled && isInsulinDailyTotalsEnabled) ||
      (isMessagingEnabled && isAlertsMessageDetailsEnabled) ||
      (cgmStatsSidebarRole && isSubjectCgmEnabled) || (cgmStatsSidebarRole && isSensorCgmEnabled);

    hasAnyParticipantReports =
      isCgmStatisticsEnabled || isMeasurementsEnabled || isAgpDailyProfilesEnabled;

    hasAnyCustomReports =
      isPeriodComplianceEnabled
      || isPeriodStatisticsEnabled
      || isCgmSensorStatisticsEnabled
      || isCgmParticipantStatisticsEnabled;
  }

  function handleClickOutside(event) {
    // Check if the click is on a menu item or its children
    const isClickOnMenuItem = event.target.closest('.menu-item') !== null;
    const isClickOnSubmenu = event.target.closest('.submenu-panel') !== null;
    const isClickOnDbMenu = event.target.closest('.db-menu') !== null;

    // Only close menus if clicking on non-interactive areas
    if (!isClickOnMenuItem && !isClickOnSubmenu && !isClickOnDbMenu) {
      unblindedExpanded = false;
      supportExpanded = false;
      reportsExpanded = false;
      participantReportsExpanded = false;
      participantExpanded = false;
      helpExpanded = false;
      complianceExpanded = false;
      dbMenuExpanded = false;
    }
  }

  function getSubmenuAnchor(menuName) {
    switch (menuName) {
      case 'compliance':
        return complianceAnchor;
      case 'participantReports':
        return participantReportsAnchor;
      case 'support':
        return supportAnchor;
      case 'reports':
        return reportsAnchor;
      default:
        return null;
    }
  }

  function getSubmenuElement(menuName) {
    switch (menuName) {
      case 'compliance':
        return complianceMenuEl;
      case 'participantReports':
        return participantReportsMenuEl;
      case 'support':
        return supportMenuEl;
      case 'reports':
        return reportsMenuEl;
      default:
        return null;
    }
  }

  function updateSubmenuPosition(menuName) {
    if (typeof window === 'undefined') return;
    const anchor = getSubmenuAnchor(menuName);
    const menuEl = getSubmenuElement(menuName);
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuHeight = menuEl?.getBoundingClientRect().height ?? 0;
    const viewportHeight = window.innerHeight || 0;
    const verticalPadding = 8;
    let top = rect.top + rect.height / 2 - menuHeight / 2;
    if (menuHeight > 0 && viewportHeight > 0) {
      top = Math.min(
        viewportHeight - menuHeight - verticalPadding,
        Math.max(verticalPadding, top)
      );
    }
    submenuPositions = {
      ...submenuPositions,
      [menuName]: {
        top,
        left: rect.right,
      },
    };
  }

  function updateOpenSubmenuPositions() {
    if (complianceExpanded) updateSubmenuPosition('compliance');
    if (participantReportsExpanded) updateSubmenuPosition('participantReports');
    if (supportExpanded) updateSubmenuPosition('support');
    if (reportsExpanded) updateSubmenuPosition('reports');
  }

  async function handleMenuExpand(menuName) {
    // Close all other menus
    if (menuName !== 'unblinded') unblindedExpanded = false;
    if (menuName !== 'support') supportExpanded = false;
    if (menuName !== 'reports') reportsExpanded = false;
    if (menuName !== 'participantReports') participantReportsExpanded = false;
    if (menuName !== 'help') helpExpanded = false;
    if (menuName !== 'compliance') complianceExpanded = false;
    // Toggle the clicked menu
    switch (menuName) {
      case 'unblinded':
        unblindedExpanded = !unblindedExpanded;
        break;
      case 'support':
        supportExpanded = !supportExpanded;
        break;
      case 'reports':
        reportsExpanded = !reportsExpanded;
        break;
      case 'participantReports':
        participantReportsExpanded = !participantReportsExpanded;
        break;
      case 'help':
        helpExpanded = !helpExpanded;
        break;
      case 'compliance':
        complianceExpanded = !complianceExpanded;
        break;
    }

    await tick();
    updateOpenSubmenuPositions();
  }

  onMount(async () => {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('resize', updateOpenSubmenuPositions);
    }

    // Fetch databases for the switcher
    fetchDatabases();

    // If in single study mode, automatically select the study
    if ($config.singleStudyMode && $config.singleStudyId) {
      try {
        const study = await fetchStudy($config.singleStudyId);
        selectedStudy.set(study);
      } catch (error) {
        console.error('Failed to load configured study:', error);
      }
    }

    // If in single site mode, automatically select the site
    if ($config.singleSiteMode && $config.singleSiteId) {
      try {
        const site = await fetchSite($config.singleSiteId);
        selectedSite.set(site.data);
        // Only redirect to persons if we're not already on the dashboard
        if (window.location.pathname !== '/dashboard') {
          goto(`/persons`);
        }
      } catch (error) {
        selectedSite.set(null);
        console.error('Failed to load configured site:', error);
      }
    }

    // Define pages that should not be automatically redirected from
    const protectedPages = [
      '/dashboard',
      '/roles',
      '/users',
      '/languages',
      '/localization',
      '/schedulerJobs',
      '/schedulerLogs',
      '/logEntries',
      '/onDemandRequests',
    ];

    // Removed automatic redirection logic - will implement simpler approach
  });

  onDestroy(() => {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('resize', updateOpenSubmenuPositions);
    }
  });

  async function fetchSite(siteId) {
    try {
      const res = await fetch(`${API_BASE_URL}/sites/byId/${siteId}`);

      if (!res.ok) {
        throw new Error('Failed to fetch site');
      }

      const site = await res.json();

      if (site) {
        return site;
      } else {
        throw new Error('Received null site from API');
      }
    } catch (err) {
      console.error('Error fetching site:', err);
      throw err;
    }
  }

  async function fetchStudy(protocolNumber) {
    try {
      const res = await fetch(`${API_BASE_URL}/studies/protocolNumber/${protocolNumber}`);

      if (!res.ok) {
        throw new Error('Failed to fetch study');
      }

      const study = await res.json();

      if (study) {
        selectedStudy.set(study);
        return study;
      } else {
        throw new Error('Received null study from API');
      }
    } catch (err) {
      console.error('Error fetching study:', err);
      selectedStudy.set(null);
      throw err;
    }
  }

  glucoseunit.subscribe((value) => {
    currentGlucoseUnit = value;
  });

  function safeToggleTheme() {
    try {
      toggleTheme();
    } catch (error) {
      console.error('Sidebar: Error in toggleTheme:', error);
    }
  }

  function openTetrisModal() {
    showTetrisModal = true;
  }

  function closeTetrisModal() {
    showTetrisModal = false;
  }

  // Database switcher functions
  async function fetchDatabases() {
    try {
      const res = await fetch(`${API_BASE_URL}/db/list`);
      if (res.ok) {
        const data = await res.json();
        databases = data.databases || [];
        // Store just the index for reliable comparison
        currentDbIndex = data.current?.index ?? null;
        // Check if multi-db mode is enabled
        multiDbMode = data.multiDbMode === true;
        // Update the global store for Header title
        currentDatabaseStore.set(data.current);
      } else {
        console.error('Failed to fetch databases:', res.status);
      }
    } catch (e) {
      console.error('Failed to fetch databases:', e);
    }
  }

  function toggleDbMenu() {
    // Always refresh when opening to ensure current state
    if (!dbMenuExpanded) {
      fetchDatabases();
    }
    dbMenuExpanded = !dbMenuExpanded;
  }

  async function selectDatabase(db) {
    if (db.index === currentDbIndex || dbSwitching) return;
    
    dbSwitching = true;
    dbSwitchingTo = db.index;
    
    try {
      const res = await fetch(`${API_BASE_URL}/db/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ index: db.index })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        // Update both local state and global store
        currentDbIndex = data.current?.index ?? db.index;
        currentDatabaseStore.set(data.current);
        dbMenuExpanded = false;
        goto('/studies');
      }
    } catch (e) {
      console.error('Failed to switch database:', e);
    } finally {
      dbSwitching = false;
      dbSwitchingTo = null;
    }
  }

  // Subscribe to theme changes
  theme.subscribe((value) => {
    currentTheme = value;
    //console.log('Sidebar: Theme updated to', value);
  });

  // Add a subscription to log changes to selectedParticipant
  selectedParticipant.subscribe((value) => {
    //console.log('selectedParticipant updated:', value);
  });

  // Add version import
  const version = __APP_VERSION__;
  const build = __BUILD_NUMBER__;
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<nav
  bind:this={sidebarElement}
  style="position: fixed; top: 0; left: 0; bottom: 0; padding-top: 44px; z-index: 50;"
  class="bg-stone-100 dark:bg-gray-725 p-0 flex flex-col transition-all duration-300 ease-in-out {$sidebarCollapsed
    ? 'w-13'
    : 'w-52'}"
  on:click={(event) => {
    // Check if the click is on a menu item or its children
    const isClickOnMenuItem = event.target.closest('.menu-item') !== null;
    const isClickOnSubmenu = event.target.closest('.absolute') !== null;

    // Only close menus if clicking on non-interactive areas
    if (!isClickOnMenuItem && !isClickOnSubmenu) {
      unblindedExpanded = false;
      supportExpanded = false;
      reportsExpanded = false;
      participantExpanded = false;
      helpExpanded = false;
      complianceExpanded = false;
    }
  }}
>
  <ul class="flex flex-col flex-grow min-h-0 overflow-visible">
    <div
      class="sidebar-scroll flex-1 min-h-0 overflow-y-auto pt-3"
      on:scroll={updateOpenSubmenuPositions}
    >
      {#if $user}
        <div>
        <MenuItem
          icon={faBars}
          text={tui($tr, $le, 'menu.menu', 'Menu')}
          href="#"
          enabled={true}
          show={true}
          onClick={toggleSidebar}
          collapsed={$sidebarCollapsed}
        />

        <!-- {#if !!$user && !hasAssignedStudies}
          <MenuItem
            icon={faClipboard}
            text={tui($tr, $le, 'menu.studies', 'Studies')}
            href="/studies"
            enabled={true}
            show={true}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
          />
        {/if} -->

        {#if !!$user && $selectedStudy && !currentStudyIsSiteScoped}
          <div class="border-t border-gray-300 dark:border-gray-500 opacity-50 my-3"></div>
          <MenuItem
            icon={faHospital}
            text={!!$selectedStudy
              ? tui($tr, $le, 'menu.study', 'Study:') + ' ' + $selectedStudy.studyName
              : tui($tr, $le, 'menu.study', 'Study')}
            href="/sites"
            enabled={!!$selectedStudy}
            show={!!$user}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
          />

          <!-- <MenuItem
            icon={faHospital}
            text={tui($tr, $le, 'menu.sites', 'Sites')}
            href="/sites"
            enabled={!!$selectedStudy}
            show={true}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
            indent="true"
          /> -->
        {/if}

        {#if $user && $selectedSite}
          <div class="border-t border-gray-300 dark:border-gray-500 opacity-50 my-3"></div>

          <MenuItem
            icon={faBuilding}
            text={!!$selectedSite
              ? tui($tr, $le, 'menu.site', 'Site:') + ' ' + $selectedSite.siteId
              : tui($tr, $le, 'menu.site', 'Site')}
            href="/persons"
            enabled={!!$selectedSite}
            show={!!$user}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
          />

          <!-- <MenuItem
            icon={faMessageMiddle}
            text="Alerts/Messages"
            href="/siteMessages"
            enabled={!!$selectedSite}
            show={!!$user && isSiteMessagingEnabled}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
            indent="true"
            counter={!!$selectedSite && $currentSiteMessageCount > 0
              ? $currentSiteMessageCount
              : null}
          /> -->

          <MenuItem
            icon={faMessageMiddle}
            text="Alerts/Messages"
            href="/siteCommunication"
            enabled={!!$selectedSite}
            show={!!$user && isSiteMessagingEnabled}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
            indent="true"
            counter={!!$selectedSite && $currentSiteMessageCount > 0
              ? $currentSiteMessageCount
              : null}
          />

          <!-- 
          <MenuItem
            icon={faUserGroup}
            text={tui($tr, $le, 'menu.participants', 'Participants')}
            href="/persons"
            enabled={!!$selectedSite}
            show={!!$user}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
            indent="true"
          /> -->
        {/if}

        {#if $user && $selectedParticipant}
          <div class="border-t border-gray-300 dark:border-gray-500 opacity-50 my-3"></div>

          <MenuItem
            icon={faUser}
            text={!!$selectedParticipant
              ? tui($tr, $le, 'menu.persons', 'Participant:') + ' ' + $selectedParticipant.subjectId
              : tui($tr, $le, 'menu.persons', 'Participant')}
            href="/person"
            enabled={!!$selectedParticipant}
            show={!!$user}
            onClick={() => handleMenuExpand(null)}
            collapsed={$sidebarCollapsed}
          />

          {#if !!$user && isScheduleEnabled}
            <MenuItem
              icon={faCalendarDays}
              text={tui($tr, $le, 'menu.schedule', 'Schedule')}
              href="/schedule"
              enabled={!!$selectedParticipant}
              show={true}
              indent="true"
              onClick={() => handleMenuExpand(null)}
              collapsed={$sidebarCollapsed}
            />
          {/if}

          {#if !!$user && isComplianceEnabled}
            <div class="relative" bind:this={complianceAnchor}>
              <MenuItem
                icon={faSquarePollVertical}
                text={tui($tr, $le, 'menu.compliance', 'Compliance')}
                onClick={() => handleMenuExpand('compliance')}
                enabled={!!$selectedParticipant}
                show={true}
                rightIcon={$sidebarCollapsed ? null : faChevronRight}
                indent="true"
                collapsed={$sidebarCollapsed}
                hasSubmenu={true}
              />
              {#if complianceExpanded}
                <div
                  class="submenu-panel fixed bg-stone-100 dark:bg-gray-600 shadow-lg rounded-r-lg w-48 py-2 z-50"
                  style={`top: ${submenuPositions.compliance?.top ?? 0}px; left: ${submenuPositions.compliance?.left ?? 0}px;`}
                  bind:this={complianceMenuEl}
                >
                  <MenuItem
                    icon={faSquarePollVertical}
                    text={tui($tr, $le, 'menu.cgmCompliance', 'CGM Compliance')}
                    href="/cgmCompliance"
                    onClick={() => handleMenuExpand('compliance')}
                    enabled={!!$selectedParticipant}
                    show={true}
                  />
                  <MenuItem
                    icon={faFileLines}
                    text={tui($tr, $le, 'menu.wearCompliance', 'Wear Compliance')}
                    href="/dailyCompliance"
                    onClick={() => handleMenuExpand('compliance')}
                    enabled={!!$selectedParticipant}
                    show={true}
                  />
                  {#if isSMBGEnabled}
                    <MenuItem
                      icon={faSquarePollVertical}
                      text={tui($tr, $le, 'menu.smbgCompliance', 'SMBG Compliance')}
                      href="/smbgCompliance"
                      onClick={() => handleMenuExpand('compliance')}
                      enabled={!!$selectedParticipant}
                      show={true}
                    />
                  {/if}
                  {#if isEventReportEnabled && canSidebarEventReport}
                    <MenuItem
                      icon={faTriangleExclamation}
                      text="Event Report"
                      href="/eventReport"
                      onClick={() => handleMenuExpand('compliance')}
                      enabled={!!$selectedParticipant}
                      show={true}
                    />
                  {/if}
                </div>
              {/if}
            </div>
          {/if}

          {#if !!$user && isMessagingEnabled}
            <MenuItem
              icon={faMessageExclamation}
              text={tui($tr, $le, 'menu.alertsMessages', 'Alerts/Messages')}
              href="/messages"
              enabled={!!$selectedParticipant}
              show={true}
              indent="true"
              onClick={() => handleMenuExpand(null)}
              collapsed={$sidebarCollapsed}
              counter={!!$selectedParticipant && $currentParticipantMessageCount > 0
                ? $currentParticipantMessageCount
                : null}
            />
          {/if}

          {#if !!$user && isDiaryEnabled}
            <MenuItem
              icon={faSquareList}
              text={tui($tr, $le, 'menu.forms', 'Forms')}
              href="/studyForms"
              enabled={!!$selectedParticipant}
              show={true}
              indent="true"
              onClick={() => handleMenuExpand(null)}
              collapsed={$sidebarCollapsed}
            />
          {/if}

          <!-- {#if hasRole('admin')}
            <MenuItem
              icon={faWeightScale}
              text={tui($tr, $le, 'menu.weight', 'Weight')}
              href="/weight"
              enabled={!!$selectedParticipant}
              show={!!$user}
              indent="true"
              onClick={() => handleMenuExpand(null)}
              collapsed={$sidebarCollapsed}
            />
          {/if} -->

          {#if canSidebarParticipantReports && hasAnyParticipantReports}
            <div class="relative" bind:this={participantReportsAnchor}>
              <MenuItem
                icon={faFileLines}
                text={tui($tr, $le, 'menu.reports', 'Reports')}
                onClick={() => handleMenuExpand('participantReports')}
                enabled={true}
                show={!!$user}
                rightIcon={$sidebarCollapsed ? null : faChevronRight}
                collapsed={$sidebarCollapsed}
                hasSubmenu={true}
                indent="true"
              />
              {#if participantReportsExpanded}
                <div
                  class="submenu-panel fixed bg-stone-100 dark:bg-gray-600 shadow-lg rounded-r-lg w-48 py-2 z-50"
                  style={`top: ${submenuPositions.participantReports?.top ?? 0}px; left: ${submenuPositions.participantReports?.left ?? 0}px;`}
                  bind:this={participantReportsMenuEl}
                >
                  {#if isCgmStatisticsEnabled}
                    <MenuItem
                      icon={faDropletPercent}
                      text={tui($tr, $le, 'menu.cgmStatistics', 'CGM Statistics')}
                      href="/cgmStats"
                      onClick={() => handleMenuExpand('participantReports')}
                      enabled={!!$selectedSite}
                      show={!!$user}
                    />
                  {/if}
                  {#if isMeasurementsEnabled}
                    <MenuItem
                      icon={faDropletPercent}
                      text={tui($tr, $le, 'menu.measurements', 'Measurements')}
                      href="/cgm"
                      onClick={() => handleMenuExpand('participantReports')}
                      enabled={!!$selectedParticipant}
                      show={!!$user}
                    />
                  {/if}
                  {#if isAgpDailyProfilesEnabled}
                    <MenuItem
                      icon={faDropletPercent}
                      text={tui($tr, $le, 'menu.agpDailyProfiles', 'AGP & Daily Profiles')}
                      href="/agp"
                      onClick={() => handleMenuExpand('participantReports')}
                      enabled={!!$selectedParticipant}
                      show={!!$user}
                    />
                  {/if}
                </div>
              {/if}
            </div>
          {/if}

          {#if canSidebarSupportTools}
            <div class="relative" bind:this={supportAnchor}>
              <MenuItem
                icon={faHeadset}
                text={tui($tr, $le, 'menu.support', 'Support')}
                onClick={() => handleMenuExpand('support')}
                enabled={true}
                show={!!$user}
                rightIcon={$sidebarCollapsed ? null : faChevronRight}
                collapsed={$sidebarCollapsed}
                indent="true"
                hasSubmenu={true}
              />
              {#if supportExpanded}
                <div
                  class="submenu-panel fixed bg-stone-100 dark:bg-gray-600 shadow-lg rounded-r-lg w-48 py-2 z-50"
                  style={`top: ${submenuPositions.support?.top ?? 0}px; left: ${submenuPositions.support?.left ?? 0}px;`}
                  bind:this={supportMenuEl}
                >
                  <MenuItem
                    icon={faMemoPad}
                    text={tui($tr, $le, 'menu.logEntries', 'Log Entries')}
                    href="/logEntries"
                    onClick={() => handleMenuExpand('support')}
                    enabled={!!$selectedParticipant}
                    show={!!$user}
                  />
                  <MenuItem
                    icon={faClipboardQuestion}
                    text={tui($tr, $le, 'menu.onDemandRequests', 'On Demand Requests')}
                    href="/onDemandRequests"
                    onClick={() => handleMenuExpand('support')}
                    enabled={!!$selectedParticipant}
                    show={!!$user}
                  />
                  <MenuItem
                    icon={faMobileScreen}
                    text={tui($tr, $le, 'menu.devices', 'Participant Devices')}
                    href="/subjectDevices"
                    onClick={() => handleMenuExpand('support')}
                    enabled={!!$selectedParticipant}
                    show={!!$user}
                  />
                  <MenuItem
                    icon={faTableList}
                    text={tui($tr, $le, 'menu.cgmActivities', 'CGM Activities')}
                    href="/subjectZeros"
                    onClick={() => handleMenuExpand('support')}
                    enabled={!!$selectedParticipant}
                    show={!!$user}
                  />
                </div>
              {/if}
            </div>
          {/if}
        {/if}

        <!-- counter={!!$selectedSite ? messageCount : 0} -->
        </div>

        {#if $user && $selectedStudy}
          {#if canSidebarGeneralReportsShell}
            {#if hasAnyGeneralReports}
              <div class="border-t border-gray-300 dark:border-gray-500 opacity-50 my-3"></div>
            <div class="relative" bind:this={reportsAnchor}>
                <MenuItem
                  icon={faFileLines}
                  text={tui($tr, $le, 'menu.generalReports', 'General Reports')}
                  onClick={() => handleMenuExpand('reports')}
                  enabled={true}
                  show={!!$user}
                  rightIcon={$sidebarCollapsed ? null : faChevronRight}
                  collapsed={$sidebarCollapsed}
                  hasSubmenu={true}
                />
                {#if reportsExpanded}
                  <div
                    class="submenu-panel fixed bg-stone-100 dark:bg-gray-600 shadow-lg rounded-r-lg w-48 py-2 z-50"
                    style={`top: ${submenuPositions.reports?.top ?? 0}px; left: ${submenuPositions.reports?.left ?? 0}px;`}
                  bind:this={reportsMenuEl}
                  >
                    {#if isCgmDetailsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.cgmDetails', 'CGM Details')}
                        href="/studyCompliance"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if isCgmDetailsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.cgmCounts', 'CGM Counts')}
                        href="/studyCgmCounts"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarGeneralReportsBlindedItems && isCgmReadingsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.cgmReadings', 'CGM Readings')}
                        href="/studyCGM"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedParticipant}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarGeneralReportsBlindedItems && isSMBGEnabled && isSmbgDetailsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.smbgDetails', 'SMBG Details')}
                        href="/studySMBGCompliance"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarGeneralReportsBlindedItems && isDiaryEnabled && isDiaryEntryDetailsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.diaryEntryDetails', 'Diary Entry Details')}
                        href="/studyForms"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarGeneralReportsBlindedItems && isDiaryEnabled && isInsulinDailyTotalsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.insulinDailyTotals', 'Insulin Daily Totals')}
                        href="/studyInsulin"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if isMessagingEnabled && isAlertsMessageDetailsEnabled}
                      <MenuItem
                        icon={faTableList}
                        text={tui($tr, $le, 'menu.alertsMessageDetails', 'Alerts/Message Details')}
                        href="/studyMessages"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarSubjectCgmReport && isSubjectCgmEnabled}
                      <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>
                      <MenuItem
                        icon={faFileShield}
                        text={tui($tr, $le, 'menu.subjectCgm', 'Subject CGM')}
                        href="/subjectStats"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarSensorCgmReport && isSensorCgmEnabled}
                      <MenuItem
                        icon={faFileShield}
                        text={tui($tr, $le, 'menu.sensorCgm', 'Sensor CGM')}
                        href="/subjectStats2"
                        onClick={() => handleMenuExpand('reports')}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                    {/if}
                    {#if canSidebarStudyAnalytics}
                      <!-- {#if hasAnyCustomReports}
                        <div
                          class="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase"
                        >
                          Custom Reports
                        </div>
                      {/if} -->
                      <div class="border-b border-stone-300 dark:border-gray-500 my-2"></div>
                      <MenuItem
                        icon={faUserGroup}
                        text="All Participants"
                        href="/allPersons"
                        onClick={() => handleMenuExpand(null)}
                        enabled={!!$selectedStudy}
                        show={!!$user}
                      />
                      {#if isPeriodComplianceEnabled}
                        <MenuItem
                          icon={faSquarePollVertical}
                          text="Period Compliance"
                          href="/complianceReport"
                          onClick={() => handleMenuExpand(null)}
                          enabled={!!$selectedStudy}
                          show={!!$user}
                        />
                      {/if}
                      {#if isPeriodStatisticsEnabled}
                        <MenuItem
                          icon={faDropletPercent}
                          text="Period Statistics"
                          href="/periodStatisticsReport"
                          onClick={() => handleMenuExpand(null)}
                          enabled={!!$selectedStudy}
                          show={!!$user}
                        />
                      {/if}
                      {#if isCgmSensorStatisticsEnabled}
                        <MenuItem
                          icon={faDropletPercent}
                          text="CGM Sensor Statistics"
                          href="/periodStatistics"
                          onClick={() => handleMenuExpand(null)}
                          enabled={!!$selectedStudy}
                          show={!!$user}
                        />
                      {/if}
                      {#if isCgmParticipantStatisticsEnabled}
                        <MenuItem
                          icon={faDropletPercent}
                          text="CGM Participant Statistics"
                          href="/periodParticipantStatistics"
                          onClick={() => handleMenuExpand(null)}
                          enabled={!!$selectedStudy}
                          show={!!$user}
                        />
                      {/if}
                    {/if}
                  </div>
                {/if}
              </div>
            {/if}
          {/if}
        {/if}
      {/if}
    </div>
  </ul>
  {#if !$sidebarCollapsed}
    <!-- mt-auto pushes the footer to the BOTTOM of the <nav> flex column
         even when the menu items above don't fill the available height
         (common for non-admin roles whose role-gated items are hidden;
         without mt-auto the About block floats up to the natural top of
         the empty area). Tested: admin (many items) + study-user (few
         items) both render with About pinned to bottom. -->
    <div
      class="sidebar-footer flex-shrink-0 mt-auto"
      style="position: absolute; bottom: 0; left: 0; right: 0; padding: 16px; text-align: right; background: inherit;"
    >
      <div class="flex flex-col items-end gap-1">
        <p class="text-xs text-black dark:text-white text-right">
          <a href="/embed/regulatory?app=app" target="_blank" class="hover:text-sky-700 dark:hover:text-sky-500 font-semibold"
              >About Summa Cloud</a
            >
        </p>
        <p class="text-xs text-black dark:text-white text-right">
          {#if hasRole(['admin', 'support']) && multiDbMode}
            <span class="relative inline-block db-menu">
              <button
                type="button"
                on:click|stopPropagation={toggleDbMenu}
                class="hover:text-sky-700 dark:hover:text-sky-500 font-semibold cursor-pointer bg-transparent border-none p-0"
              >
                SUMMA
              </button>
              {#if dbMenuExpanded}
                <div class="absolute bottom-full left-0 mb-1 bg-stone-100 dark:bg-gray-700 shadow-lg rounded-lg py-1 z-50 w-48 text-left border border-gray-200 dark:border-gray-600">
                  <div class="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">Select Database</div>
                  {#if databases.length === 0}
                    <div class="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">No databases configured</div>
                  {:else}
                    {#each databases as db (db.index)}
                      {@const isActive = db.index === currentDbIndex}
                      <button
                        type="button"
                        disabled={dbSwitching}
                        on:click|stopPropagation={() => selectDatabase(db)}
                        class="w-full px-3 py-1.5 text-left hover:bg-sky-100 dark:hover:bg-gray-600 flex items-center gap-2 disabled:opacity-50
                               {isActive ? 'bg-green-50 dark:bg-green-900/20' : ''}"
                      >
                        {#if dbSwitchingTo === db.index}
                          <svg class="animate-spin h-3 w-3 text-sky-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        {/if}
                        <span class="font-medium text-gray-800 dark:text-gray-200">{db.name}</span>
                        {#if isActive}
                          <span class="ml-auto text-green-600 dark:text-green-400 text-xs">●</span>
                        {/if}
                      </button>
                    {/each}
                  {/if}
                </div>
              {/if}
            </span>
            <a href="/about" class="hover:text-sky-700 dark:hover:text-sky-500 font-semibold"
              >Summa Cloud {version}</a
            >
          {:else}
            <span>Summa Cloud {version}</span>
          {/if}
          <br />
          Build:
          {#if canFooterChangelogLink}
            <a
              href="/changelog"
              class="hover:text-sky-700 dark:hover:text-sky-500"
              title="Changelog">{build}</a
            >
          {:else}
            <span title="Changelog">{build}</span>
          {/if}
          <br />
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          {#if hasRole(['admin', 'support'])}
            <span
              class="cursor-pointer hover:text-sky-700 dark:hover:text-sky-500 transition-colors duration-200"
              on:click={openTetrisModal}
              on:keydown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openTetrisModal();
              }}
              role="button"
              tabindex="0">&copy;</span
            >
          {:else}
            <span>&copy;</span>
          {/if}
          {new Date().getFullYear()}
          <a
            href="https://precisiondigitalhealth.com"
            target="_blank"
            class="font-semibold hover:text-sky-700 dark:hover:text-sky-500">PDH Inc.</a
          >
          <br /><br />
          <span class="text-xs text-gray-500 dark:text-gray-400" title="Disclaimer">Disclaimer: Do not use retrospective data for real-time clinical decisions</span>
        </p>
      </div>
    </div>
  {/if}
</nav>

<!-- Tetris Modal -->
<TetrisModal isOpen={showTetrisModal} on:close={closeTetrisModal} />

<style>
  /* Make nav a positioning context so .sidebar-footer's absolute
     positioning resolves to the nav box (not the viewport). */
  nav {
    position: relative;
  }

  /* The flex chain (.main-container -> .content-container -> <nav>) + the
     <ul>'s `flex-grow` SHOULD push the footer to the bottom naturally,
     but on some user machines the Tailwind `mt-auto` utility wasn't
     applying during HMR (vite/tailwind dev cache). Belt-and-suspenders:
     pin the footer to the bottom via absolute positioning. This
     guarantees it lands at the bottom regardless of flex/Tailwind state.
     The scroll area (.sidebar-scroll) needs bottom padding so the last
     menu item isn't hidden behind the footer. */
  .sidebar-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin-top: auto;            /* harmless fallback if absolute fails */
    padding: 16px;
    text-align: right;
    background: inherit;          /* match nav bg so it doesn't look detached */
  }

  /* Reserve space at the bottom of the scroll area so the last menu item
     isn't visually under the footer. Approximate the footer's height
     (4 lines × ~14px line-height + 2 × 16px padding ≈ 88px). */
  :global(.sidebar-scroll) {
    padding-bottom: 88px;
  }

  .sidebar-footer p {
    margin: 4px 0;
  }
</style>
